import { useMemo } from 'react';
import { Eye, MoreVertical, Phone } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { Table, type Column } from '@/components/ui/Table';
import { Tabs } from '@/components/ui/Tabs';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { cn } from '@/lib/utils';
import type { TransportRoute, TransportTrip } from '@/types/transport';
import { ROUTE_TONE, TRIP_KIND_LABEL, TRIP_STATUS, toClockLabel } from '../constants';

type TripView = 'today' | 'upcoming';

const VIEWS = [
  { value: 'today' as const, label: "Today's trips" },
  { value: 'upcoming' as const, label: 'Upcoming trips' },
];

const ROWS_PER_PAGE = [5, 10, 25] as const;

/**
 * The panel's filters, kept in the address under `trips.` so Back returns to
 * the same view, route and page. A route code has no allowed list: the codes
 * come with the routes, and one that matches nothing shows an empty table.
 */
type Filters = {
  view: TripView;
  routeCode: string;
  page: number;
  limit: number;
};

const DEFAULT_FILTERS: Filters = {
  view: 'today',
  routeCode: '',
  page: 1,
  limit: ROWS_PER_PAGE[0],
};

const FILTER_OPTIONS = {
  prefix: 'trips',
  allowed: {
    view: VIEWS.map((option) => option.value),
    limit: ROWS_PER_PAGE,
  },
};

export interface TripsPanelProps {
  trips: TransportTrip[];
  routes: TransportRoute[];
  onOpenTrip?: (trip: TransportTrip) => void;
}

/**
 * The day's runs, filtered and paged in the browser.
 *
 * Paging here rather than on the server is deliberate *for now*: the whole
 * section reads a mock, and a page of runs is a dozen rows. When the endpoint
 * lands this becomes server-side like the roll's — the props already carry the
 * whole list, so the change is confined to this file.
 *
 * A run that has already finished is not "upcoming", and one that has not left
 * is not "today's" business in the same way — so the two views are a genuine
 * split of the list rather than the same rows twice.
 */
export function TripsPanel({ trips, routes, onOpenTrip }: TripsPanelProps) {
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { view, routeCode, page, limit } = filters;

  const visible = useMemo(() => {
    const byView = trips.filter((trip) =>
      view === 'today'
        ? trip.status !== 'SCHEDULED'
        : trip.status === 'SCHEDULED',
    );
    return routeCode ? byView.filter((trip) => trip.routeCode === routeCode) : byView;
  }, [trips, view, routeCode]);

  const totalPages = Math.max(1, Math.ceil(visible.length / limit));
  // A filter applied while the reader is on page 3 would otherwise ask for a
  // page the narrowed list does not have, and show an empty table.
  const currentPage = Math.min(page, totalPages);
  const rows = visible.slice((currentPage - 1) * limit, currentPage * limit);

  const routeOptions = [
    { value: '', label: 'All routes' },
    ...routes.map((route) => ({ value: route.code, label: `${route.code} — ${route.name}` })),
  ];

  const columns: Column<TransportTrip>[] = [
    {
      key: 'code',
      header: 'Route no.',
      sticky: 'left',
      stickyWidth: 110,
      render: (trip) => {
        const tone = routes.find((route) => route.code === trip.routeCode)?.tone ?? 'sky';
        return (
          <span className={cn('pill text-[12px] font-bold tracking-label', ROUTE_TONE[tone].soft)}>
            {trip.routeCode}
          </span>
        );
      },
    },
    {
      key: 'route',
      header: 'Route name',
      render: (trip) => <span className="font-bold text-ink">{trip.routeName}</span>,
    },
    {
      key: 'driver',
      header: 'Driver',
      hideOnMobile: true,
      render: (trip) => (
        <div className="min-w-0">
          <p className="truncate font-bold text-ink">{trip.driverName}</p>
          <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-ink-3">
            <Phone className="h-3 w-3" aria-hidden />
            {trip.driverMobile}
          </p>
        </div>
      ),
    },
    {
      key: 'vehicle',
      header: 'Vehicle no.',
      hideOnMobile: true,
      render: (trip) => <span className="font-semibold text-ink-2">{trip.vehicleNumber}</span>,
    },
    {
      key: 'kind',
      header: 'Trip',
      render: (trip) => <Badge tone="info">{TRIP_KIND_LABEL[trip.kind]}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (trip) => (
        <Badge tone={TRIP_STATUS[trip.status].tone} withDot>
          {TRIP_STATUS[trip.status].label}
        </Badge>
      ),
    },
    {
      key: 'students',
      header: 'Students',
      align: 'center',
      hideOnMobile: true,
      render: (trip) => (
        <span className="font-semibold text-ink-2">
          {trip.studentsAboard} / {trip.studentsExpected}
        </span>
      ),
    },
    {
      key: 'departure',
      header: 'Departure',
      hideOnMobile: true,
      render: (trip) => (
        <span className="font-semibold text-ink-2 whitespace-nowrap">{toClockLabel(trip.departureTime)}</span>
      ),
    },
    {
      key: 'eta',
      header: 'ETA',
      render: (trip) => (
        <div className="whitespace-nowrap">
          <p className="font-bold text-ink whitespace-nowrap">{toClockLabel(trip.eta)}</p>
          <p
            className={cn(
              'mt-0.5 text-xs font-bold whitespace-nowrap',
              trip.delayMinutes > 0 ? 'text-warn' : 'text-ok',
            )}
          >
            {trip.delayMinutes > 0 ? `${trip.delayMinutes} min late` : 'On time'}
          </p>
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      sticky: 'right',
      stickyWidth: 90,
      render: (trip) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => onOpenTrip?.(trip)}
            title={`Open ${trip.routeName}`}
            aria-label={`Open ${trip.routeName}`}
            className="grid h-8 w-8 place-items-center rounded-xl text-ink-3 transition-colors hover:bg-surface-2 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Eye className="h-4 w-4" />
          </button>
          <span
            className="grid h-8 w-8 place-items-center rounded-xl text-ink-3"
            aria-hidden
            title="More actions arrive with the live data"
          >
            <MoreVertical className="h-4 w-4" />
          </span>
        </div>
      ),
    },
  ];

  return (
    <section className="rounded-3xl border border-line bg-surface shadow-sm">
      <header className="flex flex-col gap-3 border-b border-line px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs
          options={VIEWS}
          value={view}
          onChange={(next) => setFilters({ view: next, page: 1 })}
          label="Trip views"
        />

        <div className="w-full sm:w-56">
          <Select
            label="Route"
            hideLabel
            options={routeOptions}
            value={routeCode}
            onChange={(event) => setFilters({ routeCode: event.target.value, page: 1 })}
          />
        </div>
      </header>

      <Table
        columns={columns}
        rows={rows}
        rowKey={(trip) => trip.uuid}
        variant="plain"
        empty={
          <EmptyState
            title={view === 'today' ? 'No runs today' : 'Nothing scheduled'}
            description={
              routeCode
                ? 'No trip on this route matches that view. Try another route.'
                : 'Trips appear here once routes have vehicles and drivers against them.'
            }
          />
        }
      />

      {visible.length > 0 && (
        <Pagination
          meta={{ page: currentPage, limit, total: visible.length, totalPages }}
          onPageChange={(next) => setFilters({ page: next })}
          itemLabel="trips"
          showPageNumbers
          limitOptions={ROWS_PER_PAGE}
          onLimitChange={(next) => setFilters({ limit: next, page: 1 })}
        />
      )}
    </section>
  );
}
